/**
 * Jetons d'accès aux données personnelles, gardés en `sessionStorage`.
 *
 * `sessionStorage` et non `localStorage` : un jeton ouvre un dossier sans
 * redemander de nom, il n'a pas à survivre à la fermeture de l'onglet — surtout
 * sur un poste partagé. Il est rangé par portée ET par sujet : un même
 * navigateur peut avoir ouvert plusieurs devis et plusieurs réservations.
 */
export type PersonalDataScope = 'estimate' | 'quote' | 'reservation'

const storageKey = (scope: PersonalDataScope, subjectId: string) =>
  `massilia.${scope}-access.${subjectId}`

export const readAccessToken = (scope: PersonalDataScope, subjectId: string): null | string => {
  try {
    return globalThis.sessionStorage?.getItem(storageKey(scope, subjectId)) ?? null
  } catch {
    // Navigation privée stricte, stockage désactivé : on retombe simplement sur
    // la vérification par nom.
    return null
  }
}

export const writeAccessToken = (
  scope: PersonalDataScope,
  subjectId: string,
  token: string
): void => {
  try {
    globalThis.sessionStorage?.setItem(storageKey(scope, subjectId), token)
  } catch {
    /* voir readAccessToken */
  }
}

export const clearAccessToken = (scope: PersonalDataScope, subjectId: string): void => {
  try {
    globalThis.sessionStorage?.removeItem(storageKey(scope, subjectId))
  } catch {
    /* voir readAccessToken */
  }
}

/** En-têtes d'un appel `/public/*` : le jeton du sujet visé, et lui seul. */
export const accessAuthHeaders = (
  scope: PersonalDataScope,
  subjectId: string
): Record<string, string> => {
  const token = readAccessToken(scope, subjectId)

  return token ? { Authorization: `Bearer ${token}` } : {}
}
